import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';

/**
 * SD-1235: drag a <th>'s right edge to resize its column — what ngx-datatable's resize handle
 * does, for the plain-table lists (the Transactions ledger).
 *
 *   <th appColumnResize [minWidth]="80">Date</th>
 *
 * Draws a grip on the cell's right edge (styled as .col-resize-handle in _list-table.scss).
 * The drag runs outside Angular, so it costs no change detection per pixel. Clicks on the grip
 * are swallowed so a resize never also sorts the column.
 */
@Directive({ selector: '[appColumnResize]' })
export class ColumnResizeDirective implements OnInit, OnDestroy {
  @Input() minWidth = 60;

  private handle: HTMLElement;
  private unlisten: Array<() => void> = [];

  constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2, private zone: NgZone) { }

  ngOnInit(): void {
    const th = this.el.nativeElement;
    this.handle = this.renderer.createElement('span');
    this.renderer.addClass(this.handle, 'col-resize-handle');
    this.renderer.setAttribute(this.handle, 'aria-hidden', 'true');
    this.renderer.appendChild(th, this.handle);
    this.renderer.addClass(th, 'col-resizable');

    this.zone.runOutsideAngular(() => {
      this.unlisten.push(
        this.renderer.listen(this.handle, 'pointerdown', (e: PointerEvent) => this.start(e)),
        this.renderer.listen(this.handle, 'click', (e: Event) => e.stopPropagation())
      );
    });
  }

  private start(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const th = this.el.nativeElement;
    const startX = event.clientX;
    const startWidth = th.getBoundingClientRect().width;
    this.renderer.addClass(th, 'col-resizing');
    document.body.style.cursor = 'col-resize';

    const move = (e: PointerEvent) => {
      const width = Math.max(this.minWidth, Math.round(startWidth + e.clientX - startX));
      th.style.width = `${width}px`;
      th.style.minWidth = `${width}px`;
      th.style.maxWidth = `${width}px`;
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      this.renderer.removeClass(th, 'col-resizing');
      document.body.style.cursor = '';
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  }

  ngOnDestroy(): void {
    this.unlisten.forEach(off => off());
  }
}
